import { FC, useState } from 'react';
import { CalendarIcon, Search } from 'lucide-react';
import { DateRange } from 'react-day-picker';
import { format } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Slider } from '@/components/ui/slider';
import {
  PopoverContent,
  PopoverTrigger,
  Popover,
} from '@/components/ui/popover';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

interface CollectionSearchFiltersProps {
  onSearch: (filters: CollectionSearchFilters) => void;
  loading?: boolean;
  siteDomains?: string[];
}

export interface CollectionSearchFilters {
  query: string;
  dateRange?: {
    from: Date;
    to: Date;
  };
  scoreThreshold: number;
  expandQuery: boolean;
  enableRecencyBoost: boolean;
  topK: number;
  selectedSites?: string[];
}

const getDateRange = (date: DateRange | undefined): string => {
  if (!date) {
    return '';
  }

  const fromDate = date.from ? format(date.from, 'MM/dd/yyyy') : '';
  const toDate = date.to ? format(date.to, 'MM/dd/yyyy') : '';

  if (fromDate === toDate || (fromDate && !toDate)) {
    return `${fromDate}`;
  }

  return `${fromDate} - ${toDate}`;
};

export const CollectionSearchFilters: FC<CollectionSearchFiltersProps> = ({
  onSearch,
  loading = false,
  siteDomains,
}) => {
  const [query, setQuery] = useState('');
  const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined);
  const [scoreThreshold, setScoreThreshold] = useState(0.5);
  const [expandQuery, setExpandQuery] = useState(false);
  const [enableRecencyBoost, setEnableRecencyBoost] = useState(false);
  const [topK, setTopK] = useState(50);
  const [selectedSites, setSelectedSites] = useState<string[]>([]);

  const trimmedQuery = query.trim();
  const isQueryValid = trimmedQuery.length >= 3;
  const showValidationError = trimmedQuery.length > 0 && !isQueryValid;

  const handleSearch = () => {
    if (!trimmedQuery || !isQueryValid) return;

    const filters: CollectionSearchFilters = {
      query: trimmedQuery,
      scoreThreshold,
      expandQuery,
      enableRecencyBoost,
      topK,
    };

    if (dateRange?.from && dateRange?.to) {
      filters.dateRange = {
        from: dateRange.from,
        to: dateRange.to,
      };
    }

    if (selectedSites.length > 0) {
      filters.selectedSites = selectedSites;
    }

    onSearch(filters);
  };

  const handleScoreThresholdChange = (value: number) => {
    // Snap to nearest 0.05 increment
    const rounded = Math.round(value * 20) / 20;
    setScoreThreshold(Math.max(0, Math.min(1, rounded)));
  };

  return (
    <Card className='mb-6'>
      <CardHeader>
        <div className='flex items-center gap-2'>
          <Search className='h-5 w-5 text-muted-foreground' />
          <CardTitle className='text-lg font-semibold'>
            Search Collection
          </CardTitle>
        </div>
      </CardHeader>
      <CardContent className='space-y-4'>
        {/* Query Field */}
        <div className='space-y-2'>
          <Label htmlFor='search-query'>Search Query</Label>
          <Input
            id='search-query'
            placeholder='Enter your search query...'
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleSearch();
              }
            }}
          />
          {showValidationError && (
            <p className='text-sm text-destructive'>
              Query must be at least 3 characters long.
            </p>
          )}
        </div>

        <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
          {/* Date Range */}
          <div className='space-y-2'>
            <Label>Date Range (Optional)</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant='outline'
                  className={`w-full justify-between text-left font-normal ${
                    !dateRange ? 'text-muted-foreground' : ''
                  }`}
                >
                  {dateRange ? (
                    getDateRange(dateRange)
                  ) : (
                    <span>Pick a date range</span>
                  )}
                  <CalendarIcon className='ml-2 h-4 w-4' />
                </Button>
              </PopoverTrigger>
              <PopoverContent className='w-auto p-0'>
                <Calendar
                  mode='range'
                  selected={dateRange}
                  onSelect={setDateRange}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Score Threshold */}
          <div className='space-y-2'>
            <Label htmlFor='score-threshold'>
              Score Threshold: {scoreThreshold.toFixed(2)}
            </Label>
            <div className='flex items-center space-x-3'>
              <span className='text-sm text-muted-foreground'>0.0</span>
              <Slider
                id='score-threshold'
                min={0}
                max={1}
                step={0.05}
                value={[scoreThreshold]}
                onValueChange={(values) =>
                  handleScoreThresholdChange(values[0])
                }
                className='flex-1'
              />
              <span className='text-sm text-muted-foreground'>1.0</span>
            </div>
          </div>

          {/* Top K */}
          <div className='space-y-2'>
            <Label htmlFor='top-k'>Max Results (top_k)</Label>
            <Input
              id='top-k'
              type='number'
              min={1}
              max={500}
              step={1}
              value={topK}
              onKeyDown={(e) => {
                if (['e', 'E', '+', '-', '.', ','].includes(e.key)) {
                  e.preventDefault();
                }
              }}
              onChange={(e) => {
                const value = Number(e.target.value);
                if (Number.isNaN(value)) {
                  setTopK(1);
                } else {
                  setTopK(Math.min(Math.max(value, 1), 500));
                }
              }}
            />
          </div>
        </div>

        {/* Site Filter */}
        {siteDomains && siteDomains.length > 1 && (
          <div className='space-y-2'>
            <Label>Filter by Site</Label>
            <div className='flex flex-wrap gap-3'>
              <div className='flex items-center space-x-2'>
                <Checkbox
                  id='site-all'
                  checked={selectedSites.length === 0}
                  onCheckedChange={() => setSelectedSites([])}
                />
                <Label
                  htmlFor='site-all'
                  className='text-sm font-medium leading-none cursor-pointer'
                >
                  All Sites
                </Label>
              </div>
              {siteDomains.map((domain) => (
                <div key={domain} className='flex items-center space-x-2'>
                  <Checkbox
                    id={`site-${domain}`}
                    checked={selectedSites.includes(domain)}
                    onCheckedChange={(checked) => {
                      setSelectedSites((prev) =>
                        checked
                          ? [...prev, domain]
                          : prev.filter((d) => d !== domain),
                      );
                    }}
                  />
                  <Label
                    htmlFor={`site-${domain}`}
                    className='text-sm font-medium leading-none cursor-pointer'
                  >
                    {domain}
                  </Label>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Checkboxes */}
        <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
          <div className='flex items-center space-x-2'>
            <Checkbox
              id='expand-query'
              checked={expandQuery}
              onCheckedChange={(checked) => setExpandQuery(checked === true)}
            />
            <Label
              htmlFor='expand-query'
              className='text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70'
            >
              Expand Query
            </Label>
          </div>

          <div className='flex items-center space-x-2'>
            <Checkbox
              id='enable-recency-boost'
              checked={enableRecencyBoost}
              onCheckedChange={(checked) =>
                setEnableRecencyBoost(checked === true)
              }
            />
            <Label
              htmlFor='enable-recency-boost'
              className='text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70'
            >
              Enable Recency Boost
            </Label>
          </div>
        </div>

        {/* Search Button */}
        <div className='pt-2'>
          <Button
            onClick={handleSearch}
            disabled={!trimmedQuery || !isQueryValid || loading}
            className='w-full md:w-auto'
          >
            {loading ? 'Searching...' : 'Search'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
